
import React from 'react'
import { compose } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import { MarketLineDrawLayout, MarketLineLayout } from './MarketLineLayout'
import { findCompetitorBySide } from 'smartstream-widgets/utils/competitors'
import { getMarketType, marketTypes } from 'smartstream-widgets/utils/markets'
import { getBookmakerLogoUrlByTheme, getBookmakerIconUrlByTheme } from 'smartstream-widgets/utils/logos'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { Props } from './types'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { MarketSelectionType } from 'smartstream-widgets/types/market'
import { Competitor } from 'smartstream-widgets/types/competitor'
import { getMeasurablePrice } from 'smartstream-widgets/utils/getMeasurablePrice'
import { PriceFormat } from '../Price/types'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

const styles = (theme: Theme) => createStyles({
  affiliateText: {
    color: theme.palette.grey['700'],
    fontSize: 9,
    lineHeight: 1,
    '&:first-child': {
      marginBottom: 2
    },
    '&:last-child': {
      marginTop: 2
    }
  },
  featuredLabel: {
    color: theme.palette.primary.light,
    fontSize: 8,
    lineHeight: 1,
    border: `1px solid ${theme.palette.primary.light}`,
    borderRadius: 4,
    padding: 1
  },
  featuredStar: {
    width: '9px',
    height: '9px',
    margin: `0px 5px`
  },
  logo: {
    height: 20,
    objectFit: 'contain',
    verticalAlign: 'top',
    width: 50
  },
  icon: {
    width: 24
  },
  iconOfferText: {
    fontSize: '11px',
    marginTop: '10px',
    gridArea: 'txt1-start/txt1-start/txt2-end/txt2-end',
    textAlign: 'left'
  },
  marketLabel: {
    fontSize: 7,
    textAlign: 'center',
    textTransform: 'uppercase'
  },
  oddButton: {
    minWidth: theme.spacing(11)
  },
  oddButtonWithDraw: {
    minWidth: theme.spacing(9),
    '& p': {
      fontSize: 11
    }
  },
  root: {
    padding: `0px ${theme.spacing(3)}`
  },
  rootDraw: {
    padding: `0px ${theme.spacing(2)}`,
    width: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplate: `
        "icon odds" auto
        "txt1 txt2" min-content / 40px 1fr`
  },
  rootDrawLink: {
    padding: `0px ${theme.spacing(2)}`,
    width: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateAreas: `
        "icon odds"
        "txt1 txt2"
      `,
    gridTemplateColumns: '40px 1fr',
    gridTemplateRows: 'auto min-content'
  },
  drawWithLabel: {
    paddingTop: 17,
  },
  topAffiliateContainer: {
    marginBottom: 5
  },
  topAffiliateLogo: {
    '&:nth-child(2)': {
      paddingLeft: 8
    }
  },
  captionOdd: {},
  buttonBase: {},
  bestSelection: {},
  buttonDisabled: {},
  iconContainer: {
    gridArea: 'icon'
  },
  iconLabel: {},
  iconLink: {},
  iconButton: {
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  oddsContainerDraw: {
    gridArea: 'odds'
  },
  bookmakerContainer: {
    width: 'auto'
  }
})

const isHighestPrice = (price: string | PriceFormat, highestPrice: number) => {
  const measurablePrice = getMeasurablePrice(price)
  return measurablePrice && (
    measurablePrice === highestPrice
  )
}

function findSelection(
  marketType: string | null,
  side: string,
  selections: Array<MarketSelectionType> = [],
  competitors: Array<Competitor>,
  highestPrice: number
): MarketSelectionType {
  let selection: MarketSelectionType | undefined
  if (marketType === marketTypes.overUnder) {
    const overUnderSide = side === 'HOME' ? 'Over' : 'Under'
    selection = selections.find(s => s.outcome === overUnderSide)
  } else {
    const competitor = side && findCompetitorBySide(competitors, side)
    const { competitorId = null, officialName } = competitor || {}
    selection = selections.find(s => s.competitorId === competitorId || s.name === officialName)
  }
  if (selection) {
    selection.isHighest = isHighestPrice(selection.price, highestPrice)
  }
  return selection
}

export function MarketLine(props: Props) {
  const {
    affiliateLabel,
    animationKey = '',
    bookmakers = {},
    isBookmakerLinkEnabled = true,
    classes,
    competitors,
    competitionId,
    config: { BASE_ASSET_PATH, marketLineMessage: message = '' },
    isAwayTeamFirst = false,
    labelSet = '',
    marketTypeId,
    oddsFormat,
    showAffiliateLogo,
    showIcon = false,
    showIconAlways = false,
    showIconOfferText = false,
    selections,
    sourceId,
    theme,
    translations,
    highestPrice,
    renderParameters: {
      themeMode
    } = {}
  } = props
  theme.palette.mode = themeMode || theme.palette.mode || 'light'
  const name = bookmakers[sourceId] && bookmakers[sourceId].name
  const bookmakerLink = bookmakers[sourceId] && bookmakers[sourceId].link
  const offerText = bookmakers[sourceId] && bookmakers[sourceId].offerText

  const bookmakerLogoUrl = sourceId && getBookmakerLogoUrlByTheme(bookmakers[sourceId], theme)

  const marketType = getMarketType(competitionId, marketTypeId)

  let drawSelection: MarketSelectionType

  const homeSelection = findSelection(
    marketType,
    'HOME',
    selections,
    competitors,
    highestPrice
  )
  const awaySelection = findSelection(
    marketType,
    'AWAY',
    selections,
    competitors,
    highestPrice
  )
  if (selections && selections.length > 2) drawSelection = findSelection(marketType, '', selections, competitors, highestPrice)

  const leftSelection = isAwayTeamFirst ? awaySelection : homeSelection
  const rightSelection = isAwayTeamFirst ? homeSelection : awaySelection
  const iconUrl = ((showIcon && drawSelection) || showIconAlways) ? getBookmakerIconUrlByTheme(bookmakers[sourceId], theme) : ''

  const selectionsFormatted = {
    leftSelection,
    rightSelection,
    drawSelection
  }

  const bookmakerFormatted = {
    sourceId,
    showAffiliateLogo,
    bookmakerLogoUrl,
    bookmakerLink,
    iconUrl,
    marketType,
    affiliateLabel,
    name,
    message,
    offerText
  }

  return (
    <>
      {
        leftSelection && rightSelection &&
        (drawSelection ?
          MarketLineDrawLayout(
            animationKey,
            BASE_ASSET_PATH,
            bookmakerFormatted,
            isBookmakerLinkEnabled,
            classes,
            labelSet,
            oddsFormat,
            selectionsFormatted,
            showIconOfferText,
            translations
          ) :
          MarketLineLayout(
            animationKey,
            bookmakerFormatted,
            isBookmakerLinkEnabled,
            classes,
            oddsFormat,
            selectionsFormatted,
            translations
          )
        )
      }
    </>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'MarketLine', withTheme: true, defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      featured: t('marketline.featured.translation', {
        defaultValue: 'Featured',
        context: 'Featured label in MarketLine'
      }),
      affiliateCaption: t('marketline.odds.translation', {
        defaultValue: 'Odds by',
        context: 'text to be display above affiliate logo in MarketLine'
      }),
      MONEY_LINE: t('marketline.moneyline.translation', {
        defaultValue: 'Money Line',
        context: 'Moneyline market name in MarketLine'
      }),
      OVER_UNDER: t('marketline.overunder.translation', {
        defaultValue: 'Totals',
        context: 'Over/Under market name in MarketLine'
      }),
      POINT_SPREAD: t('marketline.spread.translation', {
        defaultValue: 'Spread',
        context: 'Spread market name in MarketLine'
      }),
      away: t('marketline.labels-away.translation', {
        defaultValue: 'Away',
        context: 'Away selection label in MarketLine'
      }),
      tie: t('marketline.labels-tie.translation', {
        defaultValue: 'Tie',
        context: 'Tie selection label in MarketLine'
      }),
      home: t('marketline.labels-home.translation', {
        defaultValue: 'Home',
        context: 'Home selection label in MarketLine'
      }),
    }
  })
)(MarketLine)
